import { useState, useRef } from 'react';
import { Upload, X, Film, Check, CheckCircle, AlertCircle } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { devError } from '../utils/logger';


interface VideoUploaderProps {
  accessToken: string | null;
  projectId: string;
  onVideoUploaded?: (videoId: string) => void;
  onCancel?: () => void;
  maxSizeMB?: number;
}

type UploadStatus = 'idle' | 'uploading' | 'encoding' | 'complete' | 'error';
const MAX_VIDEO_DURATION_SECONDS = 5 * 60;

export function VideoUploader({ 
  accessToken, 
  projectId: postId, 
  onVideoUploaded,
  onCancel,
  maxSizeMB = 150 
}: VideoUploaderProps) {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedDurationSeconds, setSelectedDurationSeconds] = useState<number | null>(null);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [publicConsent, setPublicConsent] = useState(false); // ✅ NEW: Consent checkbox state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadControllerRef = useRef<AbortController | null>(null);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const readVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const cleanup = () => {
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(objectUrl);
      };

      video.onloadedmetadata = () => {
        const duration = Number.isFinite(video.duration) ? video.duration : NaN;
        cleanup();
        if (!duration || Number.isNaN(duration)) {
          reject(new Error('Kunne ikke lese videolengde'));
          return;
        }
        resolve(duration);
      };

      video.onerror = () => {
        cleanup();
        reject(new Error('Kunne ikke lese videofilen'));
      };

      video.src = objectUrl;
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/avi', 'video/x-msvideo'];
    if (!validTypes.includes(file.type)) {
      setError('Kun MP4, MOV, WebM og AVI videoer er støttet');
      return;
    }

    // Validate file size
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`Videoen er for stor. Maksimal størrelse er ${maxSizeMB}MB`);
      return;
    }

    // Validate duration
    try {
      const duration = await readVideoDuration(file);
      if (duration > MAX_VIDEO_DURATION_SECONDS) {
        setError(`Videoen er for lang (${formatDuration(duration)}). Maksimal lengde er 5:00.`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setSelectedDurationSeconds(duration);
    } catch (durationErr: any) {
      setError(durationErr?.message || 'Kunne ikke lese videolengde');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
    setError('');
    setPreviewUrl(URL.createObjectURL(file));
  };

  const startUpload = async () => {
    if (!selectedFile || !accessToken) return;

    setStatus('uploading');
    setProgress(0);
    setError('');
    let createdVideoId: string | null = null;

    try {
      // Step 1: Get upload URL from backend
      const response = await fetch(
        `${API_BASE}/videos/upload-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            projectId: postId, // ✅ FIXED: Send projectId (camelCase → middleware converts to project_id)
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            contentType: selectedFile.type,
            publicConsent: publicConsent, // ✅ NEW: Include public consent in request
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get upload URL');
      }

      const { videoId: newVideoId, uploadUrl, authorizationHeader, libraryId } = await response.json();
      createdVideoId = newVideoId;
      setVideoId(newVideoId);

      // Step 2: Upload video using TUS protocol
      uploadControllerRef.current = new AbortController();

      // Use direct PUT upload instead of TUS for simplicity
      const uploadResponse = await fetch(
        `https://video.bunnycdn.com/library/${libraryId}/videos/${newVideoId}`,
        {
          method: 'PUT',
          headers: {
            'AccessKey': authorizationHeader.replace('Bearer ', ''),
          },
          body: selectedFile,
          signal: uploadControllerRef.current.signal,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload video to Bunny.net');
      }

      setProgress(100);
      setStatus('encoding');

      // Step 3: Poll for encoding status
      pollEncodingStatus(newVideoId);

    } catch (err: any) {
      // Best-effort cleanup: prevent orphaned Bunny videos / dangling video_id on failed upload
      if (createdVideoId && accessToken) {
        try {
          await fetch(`${API_BASE}/videos/${createdVideoId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
        } catch (cleanupErr) {
          devError('Video cleanup after upload failure failed:', cleanupErr);
        }
      }

      if (err.name === 'AbortError') {
        setError('Opplasting avbrutt');
      } else {
        setError(err.message || 'Kunne ikke laste opp video');
      }
      setStatus('error');
      setVideoId(null);
      devError('Video upload error:', err);
    }
  };

  const pollEncodingStatus = async (vidId: string) => {
    const maxAttempts = 18; // 90 seconds max (5s intervals)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const statusResponse = await fetch(
          `${API_BASE}/videos/${vidId}/status`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (!statusResponse.ok) {
          const errorData = await statusResponse.json().catch(() => ({}));
          if (statusResponse.status === 422 && errorData?.errorCode === 'VIDEO_TOO_LONG') {
            throw new Error(errorData.error || 'Videoen er for lang. Maksimal lengde er 5 minutter.');
          }
          throw new Error(errorData?.error || 'Failed to get video status');
        }

        const statusData = await statusResponse.json();
        
        // Bunny status: 0=queued, 1=processing, 2=encoding, 3=finished, 4=resolution finished (playable), 5=failed
        if (statusData.status === 3 || statusData.status === 4) {
          setStatus('complete');
          setProgress(100);
          if (onVideoUploaded) {
            onVideoUploaded(vidId);
          }
          return;
        }
        if (statusData.status === 5) {
          throw new Error('Video encoding failed');
        }

        // Update progress based on encoding progress
        if (statusData.encodeProgress) {
          setProgress(Math.min(99, statusData.encodeProgress));
        }

        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        } else {
          throw new Error('Encoding tar lengre tid enn vanlig. Du kan lukke og sjekke senere.');
        }

      } catch (err: any) {
        const isTimeout = err.message?.includes('Encoding tar lengre tid');
        // On timeout: don't delete – video may still finish in background. On failed encoding: clean up.
        if (!isTimeout) {
          try {
            await fetch(`${API_BASE}/videos/${vidId}`, {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
          } catch (cleanupErr) {
            devError('Video cleanup after encoding failure failed:', cleanupErr);
          }
          setVideoId(null);
        }

        setError(err.message || 'Kunne ikke sjekke video status');
        setStatus('error');
      }
    };

    checkStatus();
  };

  const cancelUpload = () => {
    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort();
    }
    resetUploader();
  };

  const resetUploader = () => {
    setStatus('idle');
    setProgress(0);
    setError('');
    setSelectedFile(null);
    setSelectedDurationSeconds(null);
    setVideoId(null);
    setPublicConsent(false); // ✅ Reset consent checkbox
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Film className="w-5 h-5 text-purple-600" />
          Last opp video
        </h3>
        {onCancel && (
          <button
            onClick={onCancel}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* File Input */}
      {status === 'idle' && !selectedFile && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/avi"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex flex-col items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors"
          >
            <Upload className="w-12 h-12" />
            <span className="font-medium">Velg video</span>
            <span className="text-sm text-gray-500">
              MP4, MOV, WebM eller AVI (maks {maxSizeMB}MB)
            </span>
          </button>
        </div>
      )}

      {/* Selected File Preview */}
      {selectedFile && status === 'idle' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Film className="w-8 h-8 text-purple-600" />
              <div>
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB{selectedDurationSeconds !== null ? ` • ${formatDuration(selectedDurationSeconds)}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={resetUploader}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ✅ PUBLIC CONSENT CHECKBOX */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={publicConsent}
                onChange={(e) => setPublicConsent(e.target.checked)}
                className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <div className="flex-1 text-sm">
                <p className="text-gray-900 font-medium mb-1">
                  Jeg godtar at videoen blir offentlig synlig
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Ved å laste opp videoen godtar du at den vil være <strong>offentlig tilgjengelig</strong> for alle besøkende på Talentfunn.no.
                </p>
              </div>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={startUpload}
              disabled={!publicConsent}
              className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                publicConsent
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Last opp video
            </button>
            <button
              onClick={resetUploader}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Avbryt
            </button>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {(status === 'uploading' || status === 'encoding') && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Loader size="md" className="text-purple-600" />
            </div>
            <span className="text-gray-700 font-medium">
              {status === 'uploading' ? 'Laster opp...' : 'Konverterer video...'}
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-purple-600 h-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="text-sm text-gray-500 text-center">
            {progress}% fullført
          </p>

          <button
            onClick={cancelUpload}
            className="w-full py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Avbryt opplasting
          </button>
        </div>
      )}

      {/* Success State */}
      {status === 'complete' && (
        <div className="text-center space-y-3">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
          <p className="text-lg font-medium text-gray-900">Video lastet opp!</p>
          <p className="text-sm text-gray-500">
            Videoen er klar og vil vises i prosjektet ditt
          </p>
          {onCancel && (
            <button
              onClick={onCancel}
              className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Ferdig
            </button>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          {!error.includes('Encoding tar lengre tid') && <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
          <div className="flex-1">
            {!error.includes('Encoding tar lengre tid') && <p className="text-sm font-medium text-red-900">Feil</p>}
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Info Text */}
      <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
        <div className="flex items-start gap-2 mb-2">
          <Film className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-purple-900 font-medium">
            Videoinformasjon
          </p>
        </div>
        <ul className="text-sm text-purple-800 space-y-1 ml-7">
          <li>Maks én video per prosjekt</li>
          <li>Maksimal størrelse: {maxSizeMB}MB</li>
          <li>Maksimal lengde: 5 minutter</li>
          <li>Støttede formater: MP4, MOV, WebM, AVI</li>
        </ul>
      </div>
    </div>
  );
}
